import ErrorBoundary from "@/components/ErrorBoundary";
import EmploymentTable from "@/features/person/components/EmploymentTable";
import type { DeleteEmploymentParams } from "@/features/person/components/EmploymentTable";
import { useQuery } from "@apollo/client";
import { useCallback, useState } from "react";
import FacilitySideSheet from "@/features/facility/components/FacilitySideSheet";
import { GET_EMPLOYMENT_DETAILS_QUERY } from "../person/queries";
import CreateEmploymentModal from "./_components/CreateEmploymentModal";
import CreateNonMemberEmploymentModal from "./_components/CreateNonMemberEmploymentModal";
import DeleteEmploymentModal from "./_components/DeleteEmploymentModal";
import DeferredEmploymentInfoModal from "./_components/DeferredEmploymentInfoModal";
import EditEmploymentModal from "./_components/EditEmploymentModal";
import EditNonMemberEmploymentModal from "./_components/EditNonMemberEmploymentModal";
import EditEmploymentSelectionModal from "./_components/EditEmploymentSelectionModal";
import { hasDeferredPendingEmployment, isDeferredPendingEmployment } from "./_components/deferredEmployment";
import useCapabilities, { useCanManageEmployments, useCanManageNonMemberEmployments } from "@/hooks/useCapabilities";
import { usePersonCareerAccess } from "../person/_usePersonCareerAccess";
import { useNavigate, useParams } from "@/router";
import { usePollBandChanges } from "@/hooks/usePollBandChanges";
import { NoteNotableValues, ProgramStatus, ProgramTypeName } from "@/types";
import AddEditNoteSideSheet from "@/features/global/components/AddEditNoteSideSheet";
import { useNoteDeepLink } from "@/features/global/components/NotesTable/useNoteDeepLink";

type EmploymentAction = {
  employmentId: string;
  action: "view" | "edit" | "delete";
  facilityName?: string;
  jobTitle?: string;
  startDate?: string;
  endDate?: string;
};

type CreateEmploymentKind = "member" | "nonMember";

const EMPLOYMENT_REFETCH_QUERIES = [GET_EMPLOYMENT_DETAILS_QUERY];

export function getCreateEmploymentVariant(mainProgramTypeName?: string | null): CreateEmploymentKind {
  const normalizedName = mainProgramTypeName?.trim().toLowerCase();
  return normalizedName === "member" || normalizedName === "associate" ? "member" : "nonMember";
}

export default function EmploymentDetails() {
  const { loaded, canViewPersonCareer } = usePersonCareerAccess();

  const { pgaId } = useParams("/people/:pgaId/career/employment");
  const navigate = useNavigate();
  const canEdit = useCanManageEmployments();
  const canManageNonMember = useCanManageNonMemberEmployments();
  const { id: currentUserId } = useCapabilities();
  const [isAddEmploymentModalOpen, setIsAddEmploymentModalOpen] = useState(false);
  const [isEditEmploymentSelectionModalOpen, setIsEditEmploymentSelectionModalOpen] = useState(false);
  const [isDeferredEmploymentInfoModalOpen, setIsDeferredEmploymentInfoModalOpen] = useState(false);
  const [employmentIdForEdit, setEmploymentIdForEdit] = useState<string | null>(null);
  const [addNoteEmploymentId, setAddNoteEmploymentId] = useState<string | null>(null);

  const [selectedEmployment, setSelectedEmployment] = useState<EmploymentAction | null>(null);

  const { pollForBandChanges, fetchCurrentBands } = usePollBandChanges({
    onSuccess: (result) => {
      if (result.success && result.changes) {
        window.location.reload();
      }
    }
  });

  const { data, loading, error } = useQuery(GET_EMPLOYMENT_DETAILS_QUERY, {
    variables: { pgaId },
    fetchPolicy: "cache-first",
    skip: !loaded || !canViewPersonCareer || !pgaId
  });

  const employments = data?.person.employments ?? [];
  const createEmploymentVariant = data?.person
    ? getCreateEmploymentVariant(data.person.mainProgramType?.name)
    : "member";
  const employerChangeAlertPreviewEligible =
    createEmploymentVariant === "member" &&
    data?.person.mainProgramType?.name === ProgramTypeName.Member &&
    data?.person.latestProgram?.status === ProgramStatus.ACTIVE &&
    data?.person.latestProgram?.programType?.name === ProgramTypeName.Member &&
    (data?.person.primarySection?.careerConsultants?.length ?? 0) > 0;
  const employmentChangesLocked = hasDeferredPendingEmployment(employments);

  // Keep Add visible when the user has permission; deferred pending-registration
  // rows open an info modal instead of the create form (same pattern as PAT cancel).
  const canAddEmployment = createEmploymentVariant === "nonMember" ? canManageNonMember : canEdit;

  // Non-member employments are gated by their own permission; member employments
  // continue to use the standard manage-employments capability.
  const canManageEmployment = useCallback(
    (employment: { employmentKind: "MEMBER" | "NON_MEMBER" }) =>
      employment.employmentKind === "NON_MEMBER" ? canManageNonMember : canEdit,
    [canManageNonMember, canEdit]
  );

  const {
    expandRowId: highlightEmploymentId,
    highlightNoteId,
    tableRef: employmentTableRef
  } = useNoteDeepLink({ dataReady: canViewPersonCareer && employments.length > 0 });

  if (!loaded || !canViewPersonCareer) {
    return null;
  }

  const handleEmploymentClick = (employmentId: string) => {
    setSelectedEmployment({ employmentId, action: "view" });
  };

  const handleAddEmploymentClick = () => {
    if (employmentChangesLocked) {
      setIsDeferredEmploymentInfoModalOpen(true);
      return;
    }
    setIsAddEmploymentModalOpen(true);
  };

  const handleEditEmploymentClick = (employmentId: string) => {
    const employment = employments.find((e) => e.id === employmentId);

    if (employment && isDeferredPendingEmployment(employment)) {
      setIsDeferredEmploymentInfoModalOpen(true);
      return;
    }

    // Non-member employments skip the edit/add selection step
    // and open the edit modal directly.
    if (employment?.employmentKind === "NON_MEMBER") {
      setSelectedEmployment({ employmentId, action: "edit" });
      return;
    }

    setEmploymentIdForEdit(employmentId);
    setIsEditEmploymentSelectionModalOpen(true);
  };

  const handleDeleteEmployment = (params: DeleteEmploymentParams) => {
    const employment = employments.find((e) => e.id === params.employmentId);
    if (employment && isDeferredPendingEmployment(employment)) {
      setIsDeferredEmploymentInfoModalOpen(true);
      return;
    }

    setSelectedEmployment({
      employmentId: params.employmentId,
      action: "delete",
      facilityName: params.facilityName,
      jobTitle: params.jobTitle,
      startDate: params.startDate,
      endDate: params.endDate
    });
  };

  const handleSelectEdit = () => {
    if (employmentIdForEdit) {
      setSelectedEmployment({ employmentId: employmentIdForEdit, action: "edit" });
    }
    setEmploymentIdForEdit(null);
  };

  const handleSelectAdd = () => {
    if (employmentChangesLocked) {
      setIsDeferredEmploymentInfoModalOpen(true);
      setEmploymentIdForEdit(null);
      return;
    }
    setIsAddEmploymentModalOpen(true);
    setEmploymentIdForEdit(null);
  };

  const closeEditEmploymentSelectionModal = () => {
    setIsEditEmploymentSelectionModalOpen(false);
    setEmploymentIdForEdit(null);
  };

  if (error)
    return (
      <ErrorBoundary
        variant="inline"
        title="Failed to Load Employment Details"
        message={error.message}
        className="mt-12"
      />
    );

  return (
    <>
      <FacilitySideSheet
        open={selectedEmployment?.action === "view"}
        onClose={() => setSelectedEmployment(null)}
        employmentId={selectedEmployment?.action === "view" ? selectedEmployment.employmentId : undefined}
      />

      {selectedEmployment?.action === "edit" &&
        (() => {
          const employmentToEdit = employments.find((e) => e.id === selectedEmployment.employmentId);
          const isNonMember = employmentToEdit?.employmentKind === "NON_MEMBER";

          if (isNonMember) {
            return (
              <EditNonMemberEmploymentModal
                employmentId={selectedEmployment.employmentId}
                onClose={() => setSelectedEmployment(null)}
                personId={data!.person.id}
              />
            );
          }

          return (
            <EditEmploymentModal
              employmentId={selectedEmployment.employmentId}
              onClose={() => setSelectedEmployment(null)}
              personId={data!.person.id}
              pollForBandChanges={pollForBandChanges}
              fetchCurrentBands={fetchCurrentBands}
            />
          );
        })()}

      {isAddEmploymentModalOpen &&
        (createEmploymentVariant === "nonMember" ? (
          <CreateNonMemberEmploymentModal
            onClose={() => setIsAddEmploymentModalOpen(false)}
            personId={data?.person.id}
          />
        ) : (
          <CreateEmploymentModal
            personId={data?.person.id}
            onClose={() => setIsAddEmploymentModalOpen(false)}
            employments={employments}
            pollForBandChanges={pollForBandChanges}
            fetchCurrentBands={fetchCurrentBands}
            employerChangeAlertPreviewEligible={employerChangeAlertPreviewEligible}
          />
        ))}

      {selectedEmployment?.action === "delete" && (
        <DeleteEmploymentModal
          open
          onClose={() => setSelectedEmployment(null)}
          employmentId={selectedEmployment.employmentId}
          facilityName={selectedEmployment.facilityName}
          jobTitle={selectedEmployment.jobTitle}
          startDate={selectedEmployment.startDate}
          endDate={selectedEmployment.endDate}
        />
      )}

      <EditEmploymentSelectionModal
        open={isEditEmploymentSelectionModalOpen}
        onClose={closeEditEmploymentSelectionModal}
        onSelectEdit={handleSelectEdit}
        onSelectAdd={handleSelectAdd}
      />

      {isDeferredEmploymentInfoModalOpen && (
        <DeferredEmploymentInfoModal
          open={isDeferredEmploymentInfoModalOpen}
          onClosed={() => setIsDeferredEmploymentInfoModalOpen(false)}
        />
      )}

      {addNoteEmploymentId && (
        <AddEditNoteSideSheet
          open={!!addNoteEmploymentId}
          onClose={() => setAddNoteEmploymentId(null)}
          notableId={addNoteEmploymentId}
          notableType={NoteNotableValues.EMPLOYMENT}
          refetchQueries={EMPLOYMENT_REFETCH_QUERIES}
          currentUserId={currentUserId || undefined}
          pgaId={pgaId}
          objectType="person"
          onSaveSuccess={() => setAddNoteEmploymentId(null)}
        />
      )}

      <EmploymentTable
        employments={employments}
        loading={loading}
        canEdit={canEdit}
        canManageEmployment={canManageEmployment}
        canAddEmployment={canAddEmployment}
        pgaId={pgaId}
        onViewFacility={(facilityId) => navigate("/facilities/:facilityId", { params: { facilityId } })}
        onClickEmployment={handleEmploymentClick}
        onAddEmployment={handleAddEmploymentClick}
        onEditEmployment={handleEditEmploymentClick}
        onDeleteEmployment={handleDeleteEmployment}
        onAddNote={(employmentId) => setAddNoteEmploymentId(employmentId)}
        refetchQueries={EMPLOYMENT_REFETCH_QUERIES}
        highlightEmploymentId={highlightEmploymentId}
        highlightNoteId={highlightNoteId}
        tableRef={employmentTableRef}
        showHeading={false}
      />
    </>
  );
}
